import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  ScrollView,
  TextInput,
  Platform,
  Modal,
  RefreshControl,
  Linking,
  Pressable,
  Image,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { 
  getPendingClubs,
  getClubsByStatus,
  getPendingClubsCount,
  approveClub,
  rejectClub,
  grantClubPro,
  bulkApproveClubs,
  bulkRejectClubs,
  getClubApplication,
  getPendingClaimRequests,
  getClaimRequestsByStatus,
  approveClaimRequest,
  rejectClaimRequest,
} from '../services/database';
import {
  sendClubApprovalEmail,
  sendClubRejectionEmail,
  sendBulkApprovalEmails,
  sendBulkRejectionEmails,
} from '../services/emailService';

const CLAIM_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
const APP_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];

const AdminClubApprovalsScreen = ({ navigation }) => {
  const { showToast } = useToast();
  const { appUser } = useAuth();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  
  // Data state
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingClaimCount, setPendingClaimCount] = useState(0);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [claimStatusFilter, setClaimStatusFilter] = useState('PENDING');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  const [claimRequests, setClaimRequests] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  
  // All claim user IDs (for filtering applications)
  const [allClaimUserIds, setAllClaimUserIds] = useState(new Set());
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  
  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [bulkRejectTarget, setBulkRejectTarget] = useState(null);
  const [showClaimDetailModal, setShowClaimDetailModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimRejectReason, setClaimRejectReason] = useState('');

  const isAdmin = appUser?.isAdmin === true;

  // ─── Accent & status colors ──────────────────────────
  const accent = '#94EB98';
  const accentDark = '#7BC97F';
  const statusColors = {
    PENDING: '#FF9800',
    APPROVED: '#4CAF50',
    REJECTED: '#EF5350',
  };

  // ─── Fetch helpers ───────────────────────────────────
  // Fetch ALL claim user IDs (any status) to exclude from Applications
  const fetchAllClaimUserIds = useCallback(async () => {
    try {
      const all = await getClaimRequestsByStatus('ALL');
      const ids = new Set((all || []).map(c => c.requester_user_id).filter(Boolean));
      setAllClaimUserIds(ids);
      return ids;
    } catch (_) {
      return new Set();
    }
  }, []);

  const fetchClaimRequests = useCallback(async () => {
    try {
      setLoadingClaims(true);
      const requests = await getClaimRequestsByStatus(claimStatusFilter);
      setClaimRequests(requests || []);
      return requests || [];
    } catch (error) {
      console.error('Error fetching claim requests:', error);
      if (error.message && !error.message.includes('does not exist') && !error.message.includes('relation') && !error.message.includes('club_claim_requests')) {
        showToast('Failed to load claim requests', 'error');
      } else {
        setClaimRequests([]);
      }
      return [];
    } finally {
      setLoadingClaims(false);
    }
  }, [claimStatusFilter]);

  const fetchClubs = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedClubs = await getClubsByStatus(statusFilter, { search: searchQuery || null });
      // Use the latest all-claim user IDs to filter out claim-based clubs
      const latestIds = await fetchAllClaimUserIds();
      const filtered = fetchedClubs.filter(club => !latestIds.has(club.id));
      setClubs(filtered);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      showToast('Failed to load clubs', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, searchQuery]);

  const fetchPendingCount = async () => {
    try {
      const totalCount = await getPendingClubsCount();
      let claimUserCount = 0;
      try {
        const allClaims = await getClaimRequestsByStatus('ALL');
        const uniqueClaimUserIds = new Set(allClaims.map(c => c.requester_user_id).filter(Boolean));
        claimUserCount = uniqueClaimUserIds.size;
        // Also get pending claim count
        const pendingClaims = allClaims.filter(c => c.status === 'PENDING');
        setPendingClaimCount(pendingClaims.length);
      } catch (_) {}
      setPendingCount(Math.max(0, totalCount - claimUserCount));
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  // ─── Effects ─────────────────────────────────────────
  useEffect(() => {
    setShowDetailModal(false);
    setShowRejectModal(false);
    setShowClaimDetailModal(false);
    setSelectedClub(null);
    setSelectedClaim(null);
    
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigation.goBack();
      return;
    }
    fetchClubs();
    fetchClaimRequests();
    fetchPendingCount();
  }, [isAdmin]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (isAdmin) {
        fetchClubs();
        fetchClaimRequests();
        fetchPendingCount();
      }
    });
    return unsubscribe;
  }, [navigation, isAdmin]);

  useEffect(() => {
    if (isAdmin) fetchClubs();
  }, [statusFilter, isAdmin]);

  useEffect(() => {
    if (isAdmin) fetchClaimRequests();
  }, [claimStatusFilter, isAdmin]);

  useEffect(() => {
    if (activeTab === 'claims') {
      setSelectMode(false);
      setSelectedIds(new Set());
    }
    setShowDetailModal(false);
    setShowClaimDetailModal(false);
    setShowRejectModal(false);
  }, [activeTab]);

  // ─── Actions ─────────────────────────────────────────
  const handleSearch = () => fetchClubs();

  const handleRefresh = () => {
    setRefreshing(true);
    fetchClubs();
    fetchClaimRequests();
    fetchPendingCount();
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(selectedIds.size === clubs.length ? new Set() : new Set(clubs.map(c => c.id)));
  };

  const openClubDetail = async (club) => {
    setShowRejectModal(false);
    setShowClaimDetailModal(false);
    try {
      const fullClub = await getClubApplication(club.id);
      setSelectedClub(fullClub);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching club details:', error);
      showToast('Failed to load club details', 'error');
    }
  };

  const handleGrantPro = async (club) => {
    if (!appUser?.clerkId) { showToast('Admin session missing.', 'error'); return; }
    try {
      setActionInProgress(true);
      await grantClubPro(club.id, appUser.clerkId);
      showToast(`${club.club_name || club.username} is now Pro`, 'success');
      setSelectedClub({ ...club, tier: 'PRO' });
      fetchClubs();
    } catch (error) {
      showToast(error?.message || 'Failed to grant Pro', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleApprove = async (club) => {
    try {
      setActionInProgress(true);
      await approveClub(club.id, appUser.id);
      try { await sendClubApprovalEmail(club.email, club.club_name); } catch (_) {}
      showToast(`${club.club_name} approved!`, 'success');
      setShowDetailModal(false);
      fetchClubs();
      fetchPendingCount();
    } catch (error) {
      showToast('Failed to approve club', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const openRejectModal = (club, isBulk = false) => {
    setShowDetailModal(false);
    setShowClaimDetailModal(false);
    setSelectedClub(club);
    setBulkRejectTarget(isBulk ? 'bulk' : null);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { showToast('Please provide a reason', 'error'); return; }
    try {
      setActionInProgress(true);
      if (bulkRejectTarget === 'bulk') {
        const ids = Array.from(selectedIds);
        const clubsToNotify = clubs.filter(c => selectedIds.has(c.id));
        await bulkRejectClubs(ids, appUser.id, rejectionReason.trim());
        try { await sendBulkRejectionEmails(clubsToNotify.map(c => ({ email: c.email, clubName: c.club_name })), rejectionReason.trim()); } catch (_) {}
        showToast(`${ids.length} club(s) rejected`, 'success');
        setSelectedIds(new Set());
        setSelectMode(false);
      } else {
        await rejectClub(selectedClub.id, appUser.id, rejectionReason.trim());
        try { await sendClubRejectionEmail(selectedClub.email, selectedClub.club_name, rejectionReason.trim()); } catch (_) {}
        showToast(`${selectedClub.club_name} rejected`, 'success');
      }
      setShowRejectModal(false);
      setShowDetailModal(false);
      fetchClubs();
      fetchPendingCount();
    } catch (error) {
      showToast('Failed to reject club', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) { showToast('No clubs selected', 'warning'); return; }
    try {
      setActionInProgress(true);
      const ids = Array.from(selectedIds);
      const clubsToNotify = clubs.filter(c => selectedIds.has(c.id));
      await bulkApproveClubs(ids, appUser.id);
      try { await sendBulkApprovalEmails(clubsToNotify.map(c => ({ email: c.email, clubName: c.club_name }))); } catch (_) {}
      showToast(`${ids.length} club(s) approved!`, 'success');
      setSelectedIds(new Set());
      setSelectMode(false);
      fetchClubs();
      fetchPendingCount();
    } catch (error) {
      showToast('Failed to approve clubs', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleBulkReject = () => {
    if (selectedIds.size === 0) { showToast('No clubs selected', 'warning'); return; }
    openRejectModal(null, true);
  };

  const openClaimDetail = (claim) => {
    setShowDetailModal(false);
    setShowRejectModal(false);
    setSelectedClaim(claim);
    setClaimRejectReason('');
    setShowClaimDetailModal(true);
  };

  const handleApproveClaim = async () => {
    if (!selectedClaim) return;
    try {
      setActionInProgress(true);
      const clubId = selectedClaim.club_id || selectedClaim.club?.id || null;
      await approveClaimRequest(selectedClaim.id, clubId, appUser.id);
      showToast('Claim approved!', 'success');
      setShowClaimDetailModal(false);
      fetchClaimRequests();
      fetchPendingCount();
    } catch (error) {
      showToast('Failed to approve claim', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRejectClaim = async () => {
    if (!selectedClaim) return;
    try {
      setActionInProgress(true);
      await rejectClaimRequest(selectedClaim.id, claimRejectReason.trim() || 'No reason provided');
      showToast('Claim rejected', 'success');
      setShowClaimDetailModal(false);
      fetchClaimRequests();
      fetchPendingCount();
    } catch (error) {
      showToast('Failed to reject claim', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // ─── Render helpers ──────────────────────────────────
  const StatusPill = ({ status }) => {
    const bg = (statusColors[status] || '#666') + '20';
    const fg = statusColors[status] || '#666';
    return (
      <View style={[s.statusPill, { backgroundColor: bg }]}>
        <View style={[s.statusDot, { backgroundColor: fg }]} />
        <Text style={[s.statusPillText, { color: fg }]}>{status}</Text>
      </View>
    );
  };

  const renderClubCard = ({ item }) => (
    <TouchableOpacity
      style={[s.card, { backgroundColor: colors.surface, borderColor: colors.border }]}
      onPress={() => selectMode ? toggleSelection(item.id) : openClubDetail(item)}
      activeOpacity={0.7}
    >
      {selectMode && (
        <TouchableOpacity style={s.checkbox} onPress={() => toggleSelection(item.id)}>
          <Icon name={selectedIds.has(item.id) ? 'check-box' : 'check-box-outline-blank'} size={22} color={selectedIds.has(item.id) ? accent : colors.textSecondary} />
        </TouchableOpacity>
      )}
      <View style={s.cardBody}>
        <View style={s.cardTopRow}>
          <Text style={[s.cardTitle, { color: colors.text }]} numberOfLines={1}>{item.club_name || item.username}</Text>
          <StatusPill status={item.approval_status} />
        </View>
        <View style={s.cardMeta}>
          <Icon name="email" size={13} color={colors.textSecondary} />
          <Text style={[s.cardMetaText, { color: colors.textSecondary }]} numberOfLines={1}>{item.email}</Text>
        </View>
        {item.address && (
          <View style={s.cardMeta}>
            <Icon name="location-on" size={13} color={colors.textSecondary} />
            <Text style={[s.cardMetaText, { color: colors.textSecondary }]} numberOfLines={1}>{item.address}</Text>
          </View>
        )}
        <View style={s.cardMeta}>
          <Icon name="schedule" size={13} color={colors.textSecondary} />
          <Text style={[s.cardMetaText, { color: colors.textSecondary }]}>{formatDate(item.submitted_at)}</Text>
        </View>
        {item.submission_pitch && (
          <Text style={[s.cardPitch, { color: colors.textSecondary }]} numberOfLines={2}>{item.submission_pitch}</Text>
        )}
      </View>
      {!selectMode && statusFilter === 'PENDING' && (
        <View style={s.quickActions}>
          <TouchableOpacity style={[s.qButton, { backgroundColor: '#4CAF50' }]} onPress={(e) => { e.stopPropagation(); handleApprove(item); }}>
            <Icon name="check" size={18} color="white" />
          </TouchableOpacity>
          <TouchableOpacity style={[s.qButton, { backgroundColor: '#EF5350' }]} onPress={(e) => { e.stopPropagation(); openRejectModal(item); }}>
            <Icon name="close" size={18} color="white" />
          </TouchableOpacity>
        </View>
      )}
    </TouchableOpacity>
  );

  const renderClaimCard = ({ item }) => {
    const targetName = item.hotspot?.name || item.club?.club_name || item.club?.username || 'Club';
    const targetAddress = item.hotspot?.address || item.club?.address || null;
    const targetType = item.hotspot ? (item.hotspot.claimable_type === 'OFFICIAL_CLUB' ? 'Official club' : 'Venue') : 'Club';
    const claimStatus = item.status || 'PENDING';

    return (
      <TouchableOpacity
        style={[s.card, { backgroundColor: colors.surface, borderColor: colors.border }]}
        onPress={() => openClaimDetail(item)}
        activeOpacity={0.7}
      >
        <View style={s.cardBody}>
          <View style={s.cardTopRow}>
            <StatusPill status={claimStatus} />
            <Text style={[s.cardDateSmall, { color: colors.textLight }]}>{formatDate(item.created_at)}</Text>
          </View>
          <Text style={[s.cardTitle, { color: colors.text, marginTop: 6 }]} numberOfLines={1}>{targetName}</Text>
          <View style={s.cardMeta}>
            <Icon name="category" size={13} color={colors.textSecondary} />
            <Text style={[s.cardMetaText, { color: colors.textSecondary }]}>{targetType}{targetAddress ? ` · ${targetAddress}` : ''}</Text>
          </View>
          <View style={s.cardMeta}>
            <Icon name="person" size={13} color={colors.textSecondary} />
            <Text style={[s.cardMetaText, { color: colors.textSecondary }]}>{item.requester_name} · {item.requester_email}</Text>
          </View>
          {item.proof_description && (
            <Text style={[s.cardPitch, { color: colors.textSecondary }]} numberOfLines={2}>{item.proof_description}</Text>
          )}
        </View>
        <View style={s.chevronWrap}>
          <Icon name="chevron-right" size={22} color={colors.textLight} />
        </View>
      </TouchableOpacity>
    );
  };

  // ─── Modals ──────────────────────────────────────────
  const ModalShell = ({ visible, onClose, title, children, footer }) => (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <Pressable style={s.overlay} onPress={onClose}>
        <Pressable style={[s.modalBox, { backgroundColor: colors.background }]} onPress={(e) => e.stopPropagation()}>
          <View style={[s.modalHead, { borderBottomColor: colors.border }]}>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text style={[s.modalHeadTitle, { color: colors.text }]}>{title}</Text>
            <View style={{ width: 22 }} />
          </View>
          <ScrollView style={s.modalBody} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 24 }}>
            {children}
          </ScrollView>
          {footer && <View style={[s.modalFoot, { borderTopColor: colors.border, backgroundColor: colors.surface }]}>{footer}</View>}
        </Pressable>
      </Pressable>
    </Modal>
  );

  const DetailSection = ({ title, children, icon }) => (
    <View style={[s.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      {title && <Text style={[s.sectionTitle, { color: colors.text }]}>{icon ? `${icon}  ${title}` : title}</Text>}
      {children}
    </View>
  );

  const DetailRow = ({ label, value }) => (
    <View style={[s.detailRow, { borderBottomColor: colors.borderLight }]}>
      <Text style={[s.detailLabel, { color: colors.textSecondary }]}>{label}</Text>
      <Text style={[s.detailValue, { color: colors.text }]}>{value || 'N/A'}</Text>
    </View>
  );

  const renderDetailModal = () => (
    <ModalShell
      visible={showDetailModal}
      onClose={() => setShowDetailModal(false)}
      title="Club Details"
      footer={selectedClub?.approval_status === 'PENDING' ? (
        <>
          <TouchableOpacity style={[s.footBtn, { backgroundColor: '#EF5350' }]} onPress={() => openRejectModal(selectedClub)} disabled={actionInProgress}>
            <Icon name="cancel" size={18} color="white" />
            <Text style={s.footBtnText}>Reject</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.footBtn, { backgroundColor: '#4CAF50' }]} onPress={() => handleApprove(selectedClub)} disabled={actionInProgress}>
            {actionInProgress ? <LoadingAnimation size={20} /> : <><Icon name="check-circle" size={18} color="white" /><Text style={s.footBtnText}>Approve</Text></>}
          </TouchableOpacity>
        </>
      ) : selectedClub?.approval_status === 'APPROVED' && selectedClub?.tier !== 'PRO' ? (
        <TouchableOpacity style={[s.footBtn, { backgroundColor: '#2196F3', flex: 1 }]} onPress={() => handleGrantPro(selectedClub)} disabled={actionInProgress}>
          {actionInProgress ? <LoadingAnimation size={20} /> : <><Icon name="star" size={18} color="white" /><Text style={s.footBtnText}>Grant Pro (free)</Text></>}
        </TouchableOpacity>
      ) : null}
    >
      {selectedClub && (
        <>
          <DetailSection>
            <View style={{ alignSelf: 'flex-start' }}>
              <StatusPill status={selectedClub.approval_status} />
            </View>
          </DetailSection>
          <DetailSection title="Basic Information" icon="📋">
            <DetailRow label="Club Name" value={selectedClub.club_name} />
            <DetailRow label="Username" value={selectedClub.username} />
            <DetailRow label="Email" value={selectedClub.email} />
            <DetailRow label="Address" value={selectedClub.address} />
          </DetailSection>
          <DetailSection title="Submission Pitch" icon="📝">
            <View style={[s.pitchBox, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}>
              <Text style={[s.pitchText, { color: colors.text }]}>{selectedClub.submission_pitch || 'No pitch provided'}</Text>
            </View>
          </DetailSection>
          <DetailSection title="Social Links" icon="🔗">
            {selectedClub.website && <SocialRow icon="language" color="#42A5F5" label={selectedClub.website} url={selectedClub.website} />}
            {selectedClub.instagram_url && <SocialRow icon="camera-alt" color="#E91E63" label={selectedClub.instagram_url} url={selectedClub.instagram_url} />}
            {selectedClub.twitter_url && <SocialRow icon="alternate-email" color="#1DA1F2" label={selectedClub.twitter_url} url={selectedClub.twitter_url} />}
            {selectedClub.facebook_url && <SocialRow icon="facebook" color="#4267B2" label={selectedClub.facebook_url} url={selectedClub.facebook_url} />}
            {selectedClub.tiktok_url && <SocialRow icon="music-note" color="#FF004F" label={selectedClub.tiktok_url} url={selectedClub.tiktok_url} />}
            {!selectedClub.website && !selectedClub.instagram_url && !selectedClub.twitter_url && !selectedClub.facebook_url && !selectedClub.tiktok_url && (
              <Text style={[s.noData, { color: colors.textLight }]}>No social links provided</Text>
            )}
          </DetailSection>
          <DetailSection title="Timeline" icon="📅">
            <DetailRow label="Created" value={formatDate(selectedClub.created_at)} />
            <DetailRow label="Submitted" value={formatDate(selectedClub.submitted_at)} />
            {selectedClub.reviewed_at && <DetailRow label="Reviewed" value={formatDate(selectedClub.reviewed_at)} />}
          </DetailSection>
          {selectedClub.approval_status === 'REJECTED' && selectedClub.rejection_reason && (
            <DetailSection title="Rejection Reason" icon="❌">
              <View style={[s.rejBox, { backgroundColor: isDarkMode ? 'rgba(239,83,80,0.15)' : '#FFEBEE' }]}>
                <Text style={[s.rejText, { color: isDarkMode ? '#EF9A9A' : '#C62828' }]}>{selectedClub.rejection_reason}</Text>
              </View>
            </DetailSection>
          )}
          {selectedClub.approval_status === 'APPROVED' && selectedClub.tier === 'PRO' && (
            <DetailSection>
              <View style={[s.proBadge, { backgroundColor: isDarkMode ? 'rgba(76,175,80,0.15)' : '#E8F5E9' }]}>
                <Icon name="star" size={18} color="#4CAF50" />
                <Text style={{ color: '#4CAF50', fontWeight: '700', fontSize: 14 }}>Pro Member</Text>
              </View>
            </DetailSection>
          )}
        </>
      )}
    </ModalShell>
  );

  const renderClaimDetailModal = () => (
    <ModalShell
      visible={showClaimDetailModal}
      onClose={() => setShowClaimDetailModal(false)}
      title="Claim Request"
      footer={selectedClaim?.status === 'PENDING' ? (
        <>
          <TouchableOpacity style={[s.footBtn, { backgroundColor: '#EF5350' }]} onPress={handleRejectClaim} disabled={actionInProgress}>
            <Text style={s.footBtnText}>Reject</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.footBtn, { backgroundColor: '#4CAF50' }]} onPress={handleApproveClaim} disabled={actionInProgress}>
            {actionInProgress ? <LoadingAnimation size={20} /> : <Text style={s.footBtnText}>Approve</Text>}
          </TouchableOpacity>
        </>
      ) : null}
    >
      {selectedClaim && (
        <>
          <DetailSection>
            <View style={{ alignSelf: 'flex-start' }}>
              <StatusPill status={selectedClaim.status || 'PENDING'} />
            </View>
          </DetailSection>
          <DetailSection title={selectedClaim.hotspot ? 'Spot' : 'Club'} icon="📍">
            <DetailRow label="Name" value={selectedClaim.hotspot?.name || selectedClaim.club?.club_name || selectedClaim.club?.username} />
            <DetailRow label="Address" value={selectedClaim.hotspot?.address || selectedClaim.club?.address} />
            {selectedClaim.hotspot?.city && <DetailRow label="City" value={selectedClaim.hotspot.city} />}
            {selectedClaim.hotspot?.category && <DetailRow label="Category" value={selectedClaim.hotspot.category} />}
          </DetailSection>
          <DetailSection title="Requester" icon="👤">
            <DetailRow label="Name" value={selectedClaim.requester_name} />
            <DetailRow label="Email" value={selectedClaim.requester_email} />
            {selectedClaim.contact_info && <DetailRow label="Contact" value={selectedClaim.contact_info} />}
          </DetailSection>
          <DetailSection title="Proof of Ownership" icon="📄">
            <View style={[s.pitchBox, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}>
              <Text style={[s.pitchText, { color: colors.text }]}>{selectedClaim.proof_description || 'No proof provided'}</Text>
            </View>
          </DetailSection>
          <DetailSection title={selectedClaim.evidence_urls?.length > 0 ? `Attached Documents (${selectedClaim.evidence_urls.length})` : 'Attached Documents'} icon="🖼️">
            {selectedClaim.evidence_urls?.length > 0 ? (
              <View style={s.evidenceGrid}>
                {selectedClaim.evidence_urls.map((url, idx) => (
                  <TouchableOpacity
                    key={idx}
                    style={[s.evidenceItem, { borderColor: colors.border }]}
                    onPress={() => Platform.OS === 'web' ? window.open(url, '_blank') : Linking.openURL(url)}
                  >
                    <Image source={{ uri: url }} style={s.evidenceImage} resizeMode="cover" />
                    <Text style={[s.evidenceLabel, { color: colors.textSecondary }]}>Doc {idx + 1}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <Text style={[s.noData, { color: colors.textSecondary }]}>No documents were attached with this submission.</Text>
            )}
          </DetailSection>
          {selectedClaim.status === 'PENDING' && (
            <DetailSection title="Rejection Reason (optional)">
              <TextInput
                style={[s.textArea, { color: colors.text, backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5', borderColor: colors.border }]}
                placeholder="Provide a reason if rejecting..."
                placeholderTextColor={colors.textLight}
                value={claimRejectReason}
                onChangeText={setClaimRejectReason}
                multiline
              />
            </DetailSection>
          )}
          {selectedClaim.status === 'REJECTED' && selectedClaim.rejection_reason && (
            <DetailSection title="Rejection Reason" icon="❌">
              <View style={[s.rejBox, { backgroundColor: isDarkMode ? 'rgba(239,83,80,0.15)' : '#FFEBEE' }]}>
                <Text style={[s.rejText, { color: isDarkMode ? '#EF9A9A' : '#C62828' }]}>{selectedClaim.rejection_reason}</Text>
              </View>
            </DetailSection>
          )}
        </>
      )}
    </ModalShell>
  );

  const SocialRow = ({ icon, color, label, url }) => (
    <TouchableOpacity style={s.socialRow} onPress={() => Linking.openURL(url)}>
      <Icon name={icon} size={17} color={color} />
      <Text style={[s.socialText, { color: isDarkMode ? '#64B5F6' : '#1976D2' }]} numberOfLines={1}>{label}</Text>
      <Icon name="open-in-new" size={14} color={colors.textLight} />
    </TouchableOpacity>
  );

  const renderRejectModal = () => {
    if (!bulkRejectTarget && !selectedClub) return null;
    return (
      <Modal visible={showRejectModal} animationType="fade" transparent onRequestClose={() => setShowRejectModal(false)}>
        <Pressable style={s.overlay} onPress={() => setShowRejectModal(false)}>
          <Pressable style={[s.rejectBox, { backgroundColor: colors.surface }]} onPress={(e) => e.stopPropagation()}>
            <Text style={[s.rejectTitle, { color: colors.text }]}>
              {bulkRejectTarget === 'bulk' ? `Reject ${selectedIds.size} Club(s)` : `Reject ${selectedClub?.club_name || 'Club'}`}
            </Text>
            <Text style={[s.rejectSub, { color: colors.textSecondary }]}>Provide a reason. This will be sent to the club owner.</Text>
            <TextInput
              style={[s.textArea, { color: colors.text, backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5', borderColor: colors.border, minHeight: 100 }]}
              placeholder="Reason for rejection..."
              placeholderTextColor={colors.textLight}
              value={rejectionReason}
              onChangeText={setRejectionReason}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
            <View style={s.rejectActions}>
              <TouchableOpacity style={[s.rejectBtn, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f0f0f0' }]} onPress={() => setShowRejectModal(false)} disabled={actionInProgress}>
                <Text style={[s.rejectBtnCancel, { color: colors.textSecondary }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.rejectBtn, { backgroundColor: '#EF5350', opacity: !rejectionReason.trim() ? 0.5 : 1 }]} onPress={handleReject} disabled={actionInProgress || !rejectionReason.trim()}>
                {actionInProgress ? <LoadingAnimation size={18} /> : <Text style={s.rejectBtnConfirm}>Reject</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    );
  };

  // ─── Tab pill helper ─────────────────────────────────
  const TabPill = ({ label, active, onPress, count }) => (
    <TouchableOpacity
      style={[s.tabPill, { backgroundColor: active ? accent : isDarkMode ? colors.backgroundDark : '#F3F4F6', borderColor: active ? accent : colors.border }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[s.tabPillText, { color: active ? '#0A0A0A' : colors.textSecondary }]}>{label}</Text>
      {count > 0 && (
        <View style={[s.badge, { backgroundColor: active ? '#0A0A0A' : statusColors.PENDING }]}>
          <Text style={[s.badgeText, { color: 'white' }]}>{count}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  // ─── Loading state ──────────────────────────────────
  const isLoadingActiveTab = activeTab === 'applications' ? loading : loadingClaims;
  if (isLoadingActiveTab && !refreshing) {
    return (
      <View style={[s.loadingWrap, { backgroundColor: colors.background }]}>
        <LoadingAnimation size={300} />
      </View>
    );
  }

  // ─── Main render ────────────────────────────────────
  return (
    <SafeAreaView style={[s.root, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[s.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Icon name="arrow-back" size={22} color={colors.text} />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 14 }}>
          <Text style={[s.headerTitle, { color: colors.text }]}>Club Approvals</Text>
          <Text style={[s.headerSub, { color: colors.textSecondary }]}>
            {activeTab === 'applications' ? 'Review club applications' : 'Review claim requests'}
          </Text>
        </View>
        {activeTab === 'applications' && (
          <TouchableOpacity onPress={toggleSelectMode} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Icon name={selectMode ? 'close' : 'checklist'} size={22} color={colors.text} />
          </TouchableOpacity>
        )}
      </View>

      {/* Summary Cards */}
      <View style={s.summaryRow}>
        <View style={[s.summaryCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[s.summaryLabel, { color: colors.textSecondary }]}>Pending Apps</Text>
          <Text style={[s.summaryNum, { color: colors.text }]}>{pendingCount}</Text>
        </View>
        <View style={[s.summaryCard, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[s.summaryLabel, { color: colors.textSecondary }]}>Pending Claims</Text>
          <Text style={[s.summaryNum, { color: colors.text }]}>{pendingClaimCount}</Text>
        </View>
      </View>

      {/* Primary Tabs */}
      <View style={[s.primaryTabs, { borderBottomColor: colors.border }]}>
        <TabPill label="Applications" active={activeTab === 'applications'} onPress={() => setActiveTab('applications')} count={pendingCount} />
        <TabPill label="Claim Requests" active={activeTab === 'claims'} onPress={() => setActiveTab('claims')} count={pendingClaimCount} />
      </View>

      {/* Search (apps only) */}
      {activeTab === 'applications' && (
        <View style={[s.searchWrap, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Icon name="search" size={18} color={colors.textLight} />
          <TextInput
            style={[s.searchInput, { color: colors.text }]}
            placeholder="Search clubs..."
            placeholderTextColor={colors.textLight}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSearch}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => { setSearchQuery(''); fetchClubs(); }}>
              <Icon name="close" size={18} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Status sub-tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.subTabs} contentContainerStyle={s.subTabsContent}>
        {(activeTab === 'applications' ? APP_STATUSES : CLAIM_STATUSES).map((st) => {
          const isActive = activeTab === 'applications' ? statusFilter === st : claimStatusFilter === st;
          return (
            <TouchableOpacity
              key={st}
              style={[s.subTab, { backgroundColor: isActive ? accent : isDarkMode ? colors.backgroundDark : '#F0F0F0', borderColor: isActive ? accent : colors.border }]}
              onPress={() => activeTab === 'applications' ? setStatusFilter(st) : setClaimStatusFilter(st)}
            >
              <Text style={[s.subTabText, { color: isActive ? '#0A0A0A' : colors.textSecondary }]}>{st}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Bulk Actions */}
      {activeTab === 'applications' && selectMode && selectedIds.size > 0 && (
        <View style={[s.bulkBar, { backgroundColor: isDarkMode ? 'rgba(76,175,80,0.12)' : '#E8F5E9', borderBottomColor: colors.border }]}>
          <Text style={[s.bulkCount, { color: '#4CAF50' }]}>{selectedIds.size} selected</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity style={[s.bulkBtn, { backgroundColor: '#4CAF50' }]} onPress={handleBulkApprove} disabled={actionInProgress}>
              <Icon name="check" size={16} color="white" />
              <Text style={s.bulkBtnText}>Approve</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.bulkBtn, { backgroundColor: '#EF5350' }]} onPress={handleBulkReject} disabled={actionInProgress}>
              <Icon name="close" size={16} color="white" />
              <Text style={s.bulkBtnText}>Reject</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Select All */}
      {activeTab === 'applications' && selectMode && clubs.length > 0 && (
        <TouchableOpacity style={s.selectAllRow} onPress={selectAll}>
          <Icon name={selectedIds.size === clubs.length ? 'check-box' : 'check-box-outline-blank'} size={20} color={accent} />
          <Text style={[s.selectAllText, { color: accent }]}>{selectedIds.size === clubs.length ? 'Deselect All' : 'Select All'}</Text>
        </TouchableOpacity>
      )}

      {/* List */}
      <FlatList
        data={activeTab === 'applications' ? clubs : claimRequests}
        keyExtractor={(item) => item.id}
        renderItem={activeTab === 'applications' ? renderClubCard : renderClaimCard}
        contentContainerStyle={s.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={accent} />}
        ListEmptyComponent={
          <View style={s.emptyWrap}>
            <Icon
              name={activeTab === 'applications' ? (statusFilter === 'PENDING' ? 'check-circle' : 'inbox') : (claimStatusFilter === 'PENDING' ? 'verified' : 'inbox')}
              size={56}
              color={colors.textLight}
            />
            <Text style={[s.emptyTitle, { color: colors.text }]}>
              {activeTab === 'applications'
                ? (statusFilter === 'PENDING' ? 'All caught up!' : `No ${statusFilter.toLowerCase()} applications`)
                : (claimStatusFilter === 'PENDING' ? 'No pending claims' : `No ${claimStatusFilter.toLowerCase()} claims`)}
            </Text>
            <Text style={[s.emptySub, { color: colors.textSecondary }]}>
              {activeTab === 'applications'
                ? (statusFilter === 'PENDING' ? 'All club applications have been reviewed.' : 'Try changing the filter or search query.')
                : (claimStatusFilter === 'PENDING' ? 'Claim requests will appear here when submitted.' : 'Try changing the status filter.')}
            </Text>
          </View>
        }
      />

      {/* Modals */}
      {showDetailModal && renderDetailModal()}
      {showClaimDetailModal && renderClaimDetailModal()}
      {showRejectModal && renderRejectModal()}
    </SafeAreaView>
  );
};

// ─── Styles ────────────────────────────────────────────
const s = StyleSheet.create({
  root: { flex: 1 },
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  // Header
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  headerTitle: { fontSize: 20, fontWeight: '700' },
  headerSub: { fontSize: 12, marginTop: 1 },

  // Summary
  summaryRow: { flexDirection: 'row', gap: 10, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 6 },
  summaryCard: { flex: 1, borderRadius: 14, padding: 14, borderWidth: 1 },
  summaryLabel: { fontSize: 12, fontWeight: '500' },
  summaryNum: { fontSize: 26, fontWeight: '800', marginTop: 4 },

  // Primary tabs
  primaryTabs: { flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 10, gap: 10, borderBottomWidth: 1 },
  tabPill: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 22, borderWidth: 1 },
  tabPillText: { fontSize: 13, fontWeight: '600' },
  badge: { borderRadius: 10, paddingHorizontal: 6, paddingVertical: 1, minWidth: 20, alignItems: 'center' },
  badgeText: { fontSize: 11, fontWeight: '700' },

  // Search
  searchWrap: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 16, marginTop: 12, marginBottom: 4, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, height: 42 },
  searchInput: { flex: 1, paddingVertical: 0, paddingHorizontal: 8, fontSize: 14, ...(Platform.OS === 'web' && { outlineStyle: 'none' }) },

  // Sub-tabs
  subTabs: { maxHeight: 44, marginTop: 8 },
  subTabsContent: { paddingHorizontal: 16, gap: 8 },
  subTab: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 18, borderWidth: 1 },
  subTabText: { fontSize: 13, fontWeight: '600' },

  // Bulk
  bulkBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1 },
  bulkCount: { fontSize: 14, fontWeight: '600' },
  bulkBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, gap: 4 },
  bulkBtnText: { color: 'white', fontSize: 13, fontWeight: '600' },
  selectAllRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, gap: 8 },
  selectAllText: { fontSize: 14, fontWeight: '500' },

  // Cards
  listContent: { padding: 14, paddingBottom: 100 },
  card: { flexDirection: 'row', borderRadius: 14, padding: 14, marginBottom: 10, borderWidth: 1 },
  cardBody: { flex: 1 },
  cardTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: 16, fontWeight: '700' },
  cardDateSmall: { fontSize: 11 },
  cardMeta: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 5 },
  cardMetaText: { fontSize: 12, flex: 1 },
  cardPitch: { fontSize: 12, marginTop: 8, fontStyle: 'italic', lineHeight: 17 },
  checkbox: { marginRight: 10, justifyContent: 'center' },
  quickActions: { justifyContent: 'center', gap: 6, marginLeft: 10 },
  qButton: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  chevronWrap: { justifyContent: 'center', marginLeft: 6 },

  // Status pill
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusPillText: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },

  // Empty
  emptyWrap: { alignItems: 'center', justifyContent: 'center', paddingVertical: 64 },
  emptyTitle: { fontSize: 17, fontWeight: '600', marginTop: 14 },
  emptySub: { fontSize: 13, marginTop: 6, textAlign: 'center', paddingHorizontal: 32 },

  // Overlay / Modal
  overlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 20, zIndex: 9999, ...(Platform.OS === 'web' && { position: 'fixed' }) },
  modalBox: { width: '100%', maxWidth: 540, maxHeight: Platform.OS === 'web' ? '85vh' : '90%', borderRadius: 18, overflow: 'hidden', zIndex: 10000 },
  modalHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderBottomWidth: 1 },
  modalHeadTitle: { fontSize: 17, fontWeight: '700' },
  modalBody: { flex: 1, padding: 16 },
  modalFoot: { flexDirection: 'row', gap: 10, padding: 16, borderTopWidth: 1 },
  footBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 13, borderRadius: 12, gap: 6 },
  footBtnText: { color: 'white', fontSize: 15, fontWeight: '600' },

  // Detail sections
  section: { borderRadius: 14, padding: 16, marginBottom: 12, borderWidth: 1 },
  sectionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 10 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 9, borderBottomWidth: 1 },
  detailLabel: { fontSize: 13 },
  detailValue: { fontSize: 13, fontWeight: '500', flex: 1, textAlign: 'right', marginLeft: 16 },
  pitchBox: { borderRadius: 10, padding: 12, borderLeftWidth: 3, borderLeftColor: '#94EB98' },
  pitchText: { fontSize: 14, lineHeight: 21 },
  rejBox: { borderRadius: 10, padding: 12, borderLeftWidth: 3, borderLeftColor: '#EF5350' },
  rejText: { fontSize: 14, lineHeight: 20 },
  proBadge: { flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20 },
  noData: { fontSize: 13, fontStyle: 'italic' },

  // Social
  socialRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, gap: 8 },
  socialText: { fontSize: 13, flex: 1 },

  // Evidence
  evidenceGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 6 },
  evidenceItem: { alignItems: 'center', width: 90, borderRadius: 10, borderWidth: 1, overflow: 'hidden' },
  evidenceImage: { width: 90, height: 90, backgroundColor: '#333' },
  evidenceLabel: { fontSize: 11, paddingVertical: 4 },

  // TextArea
  textArea: { borderWidth: 1, borderRadius: 10, padding: 12, fontSize: 14, minHeight: 60, textAlignVertical: 'top', ...(Platform.OS === 'web' && { outlineStyle: 'none' }) },

  // Reject modal
  rejectBox: { borderRadius: 18, padding: 24, width: '100%', maxWidth: 420 },
  rejectTitle: { fontSize: 18, fontWeight: '700', marginBottom: 6 },
  rejectSub: { fontSize: 13, marginBottom: 16, lineHeight: 19 },
  rejectActions: { flexDirection: 'row', gap: 10, marginTop: 16 },
  rejectBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  rejectBtnCancel: { fontSize: 15, fontWeight: '600' },
  rejectBtnConfirm: { fontSize: 15, fontWeight: '600', color: 'white' },
});

export default AdminClubApprovalsScreen;
